"use client";
import { ServiceType } from "@/constants/enum";
import { DayPart } from "@/dto/dayPartMenu";
import { Service } from "@/dto/service";
import { cloverColorTheme } from "@/utils/cloverColorTheme";
import {
  ROUTE_ALL,
  ROUTE_CATEGORIES_ALL,
  ROUTE_CONFIRMATION,
  ROUTE_GIFTCARD,
  ROUTE_HOME,
  ROUTE_MENU,
  ROUTE_MENU_ALL,
  ROUTE_RETAIL,
  ROUTE_SERVICES,
  ROUTE_SERVICES_ALL,
  ROUTE_SHOP,
} from "@/utils/constants";
import { getFromLocalStorage } from "@/utils/localStorageUtils";
import { buildScanToOrderSearchParams } from "@/utils/scanToOrderUtils";
import { LocalStorageKey } from "@/utils/storageKeys";
import useMediaQuery from "@/utils/useMediaQuery";
import clsx from "clsx";
import { isEmpty } from "lodash";
import { useTranslations } from "next-intl";
import { usePathname } from "next/navigation";
import { use } from "react";
import MerchantLinkButton from "../merchantLinkButton/merchantLinkButton";
import { SimpleStorefrontHours } from "./header";
import { HeaderMerchantInfoContainer } from "./headerMerchantInfoContainer";

export interface ColoHeaderProps {
  merchantLogo?: string;
  merchantName?: string;
  merchant: any;
  paymentConfigPromise?: Promise<any>;
  isDuda?: boolean;
  recalculateHeight: any;
  isScrolledDown: boolean;
  selectedMenu: DayPart | null;
  restaurantMfeEnabled?: boolean;
}

export const ColoHeader = ({
  merchantLogo,
  merchantName,
  merchant,
  paymentConfigPromise,
  isDuda,
  recalculateHeight,
  isScrolledDown,
  selectedMenu,
  restaurantMfeEnabled,
}: ColoHeaderProps) => {
  const merchantColor =
    merchant?.merchantMeta?.colorPalette?.color ||
    cloverColorTheme.Main.primaryColor;
  const isSimpleStorefront = merchant?.merchantMeta?.selectedSetup === "Simple";
  const t = useTranslations("Header");
  const pathName = usePathname();
  const isMobileScreen = useMediaQuery("(max-width: 640px)");
  const merchantPaymentConfig =
    paymentConfigPromise && use(paymentConfigPromise);
  const isPreviewSite =
    typeof window !== "undefined" &&
    window.location.hostname.startsWith("preview-");
  const isStandaloneGiftCard =
    merchant?.isStandaloneGiftCardWebsite && !isPreviewSite;

  const service = merchant?.services.find(
    (s: Service) =>
      s.type === ServiceType.PICKUP || s.type === ServiceType.WEBSITE
  );
  const currentDay = new Date()
    .toLocaleDateString("en-US", { weekday: "long" })
    .toLowerCase();
  const headerBusinessHours = merchant?.isRestaurant
    ? service?.hours
    : merchant?.businessHours?.hours;
  const todayHours = headerBusinessHours?.[currentDay] ?? [];

  const getMerchantLinkButtonHref = () => {
    if (isDuda) {
      return merchant.isRetail ? ROUTE_RETAIL : ROUTE_SERVICES;
    }
    return ROUTE_HOME;
  };
  return (
    <div
      className={clsx(
        "flex",
        "sm:flex-row",
        "gap-4",
        "px-4 sm:px-10",
        "pt-6",
        "w-full",
        "justify-between",
        "transition-all",
        "ease-out",
        "duration-[0.3s]",
        {
          "flex-col": isSimpleStorefront,
          "pt-0": isMobileScreen && isScrolledDown,
          "pt-5": isMobileScreen && !isScrolledDown,
          "max-h-0 opacity-0 absolute left-[-9999px]": isScrolledDown,
          "max-h-none relative opacity-1": !isScrolledDown,
          "pb-5": isStandaloneGiftCard,
        }
      )}
      style={{ backgroundColor: "#FBFBFB" }}
      aria-hidden={isScrolledDown}
    >
      <HeaderMerchantInfoContainer
        merchantLogo={merchantLogo}
        merchantName={merchantName}
        isSimpleStorefront={isSimpleStorefront}
        isStandaloneGiftCard={isStandaloneGiftCard}
        merchantAddress={merchant?.address}
        merchantPhone={merchant?.phone}
        merchantLocale={merchant?.locale}
        merchant={merchant}
        onLogoLoad={() => {
          if (pathName === ROUTE_CONFIRMATION) {
            setTimeout(() => recalculateHeight(), 500);
          }
        }}
        isDuda={isDuda}
      />
      {isSimpleStorefront && pathName !== ROUTE_CONFIRMATION && (
        <SimpleStorefrontHours
          merchant={merchant}
          todayHours={todayHours}
          abbreviation={merchant.timezone}
          headerBusinessHours={headerBusinessHours}
          merchantPaymentConfig={merchantPaymentConfig}
        />
      )}
      {pathName !== ROUTE_CONFIRMATION &&
        !isSimpleStorefront &&
        !isStandaloneGiftCard && (
          <HeaderLinkButton
            merchant={merchant}
            merchantColor={merchantColor}
            selectedDayPart={selectedMenu}
            restaurantMfeEnabled={restaurantMfeEnabled}
          />
        )}
      {pathName === ROUTE_CONFIRMATION && (
        <p className="hidden sm:block font-normal text-[15px]">
          <MerchantLinkButton
            merchantColor={merchantColor}
            uri={getMerchantLinkButtonHref()}
            text={t("keepBrowsing")}
          />
        </p>
      )}
    </div>
  );
};

const HeaderLinkButton = ({
  merchant,
  merchantColor,
  selectedDayPart,
  restaurantMfeEnabled,
}: any) => {
  const t = useTranslations("Header");
  const pathName = usePathname();
  const isRestaurantMfeEnabled = restaurantMfeEnabled ?? false;
  const isNotRestaurantOfferShown = !pathName
    ?.toLocaleLowerCase()
    .includes("menu") &&
    !pathName?.startsWith(ROUTE_MENU(isRestaurantMfeEnabled));
  const isNotEssentialMerchantOfferShown = !pathName
    ?.toLocaleLowerCase()
    .includes("categories");
  const isOnGiftCardPage = pathName === ROUTE_GIFTCARD;
  const scanToOrderInfo = getFromLocalStorage(
    LocalStorageKey.SCAN_TO_ORDER_INFO,
    null
  );
  let queryParams = "";
  if (scanToOrderInfo) {
    queryParams = buildScanToOrderSearchParams(scanToOrderInfo);
  }
  let menuUri = merchant?.isEssentialMerchant
    ? ROUTE_CATEGORIES_ALL
    : ROUTE_MENU_ALL(isRestaurantMfeEnabled);
  if (!isEmpty(selectedDayPart?.formattedName))
    queryParams += queryParams
      ? `&menu=${selectedDayPart.formattedName}`
      : `?menu=${selectedDayPart.formattedName}`;

  if (queryParams) {
    menuUri += queryParams;
  }

  const linkButtonConfig = (() => {
    if (merchant?.isService && merchant?.hasOnlyRetailItems) {
      return getServiceRetailLinkConfig(pathName, t);
    }
    if (merchant?.isService && !merchant?.hasOnlyRetailItems) {
      return getServiceBookingLinkConfig(pathName, t);
    }
    if (merchant?.isRetail) {
      return getRetailLinkConfig(pathName, t);
    }
    if (merchant?.isRestaurant && isNotRestaurantOfferShown) {
      return { uri: menuUri, text: t("startOrder") };
    }
    if (
      merchant?.isEssentialMerchant &&
      !merchant?.isCheckoutEnabled &&
      isNotEssentialMerchantOfferShown
    ) {
      return { uri: menuUri, text: t("viewItems") };
    }
    return null;
  })();

  function getServiceRetailLinkConfig(pathName: string | null, t: any) {
    if (pathName?.startsWith(ROUTE_SHOP)) return null;
    return { uri: ROUTE_SHOP, text: t("shopNow") };
  }

  function getServiceBookingLinkConfig(pathName: string | null, t: any) {
    if (pathName?.startsWith(ROUTE_SERVICES)) return null;
    return { uri: ROUTE_SERVICES_ALL, text: t("bookNow") };
  }

  function getRetailLinkConfig(pathName: string | null, t: any) {
    if (pathName?.startsWith(ROUTE_ALL)) return null;
    if (
      process.env.NEXT_PUBLIC_ALLOW_SHOP_AS_DEFAULT_RETAIL_ROUTE === "true" &&
      pathName?.startsWith(ROUTE_RETAIL)
    ) {
      return null;
    }
    return {
      uri:
        process.env.NEXT_PUBLIC_ALLOW_SHOP_AS_DEFAULT_RETAIL_ROUTE === "true"
          ? ROUTE_RETAIL
          : ROUTE_ALL,
      text: t("shopNow"),
    };
  }

  return (
    <div className={["flex gap-6 items-center"].join(" ")}>
      <div className={isOnGiftCardPage ? "hidden md:block" : ""}>
        {linkButtonConfig && (
          <MerchantLinkButton
            merchantColor={merchantColor}
            merchant={merchant}
            uri={linkButtonConfig.uri}
            text={linkButtonConfig.text}
          />
        )}
      </div>
    </div>
  );
};
